import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  size as sizeMiddleware,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
} from '@floating-ui/react';
import { InputContainer, InputLabel, InputError, SINGLE_LINE_FIELD_CLASS } from './InputBase';
import { Check, ChevronDown, Pin } from 'lucide-react';
import {
  getSelectorOptionStateClass,
  SELECTOR_FLYOUT_CHROME_CLASS,
  SELECTOR_OPTION_CLASS,
} from './selectorPresentation';

export interface SearchableDropdownOption {
  disabled?: boolean;
  value: string;
  label: string;
  /** Optional visible and accessible section label for related options. */
  section?: string;
  tag?: string;
  tagClassName?: string;
  /** When true, only the tag is shown in the flyout option row (label still drives the trigger and search). */
  hideOptionLabel?: boolean;
}

export type SearchableDropdownTrigger = {
  content: React.ReactNode;
  ariaLabel: string;
  className?: string;
};

interface SearchableDropdownProps {
  id: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: SearchableDropdownOption[];
  placeholder?: string;
  required?: boolean;
  error?: string;
  className?: string;
  containerClassName?: string;
  /** Extra classes applied to the trigger input */
  inputClassName?: string;
  disabled?: boolean;
  /** When false, the dropdown acts as a simple selector without a search field. If undefined, defaults to true if options > 10. */
  isSearchable?: boolean;
  /** Display a loading message while options are being refreshed */
  isLoadingOptions?: boolean;
  loadingMessage?: string;
  /** When true, the dropdown opens upwards */
  isUpward?: boolean;
  /** Size of the input */
  size?: 'sm' | 'md';
  /** Whether free-form values not present in `options` are allowed */
  allowCustomValue?: boolean;
  /** Optional checkmark indication */
  showCheckmark?: boolean;
  /** Optional footer action or content rendered beneath the options list */
  footerAction?: React.ReactNode;
  /** When true, the flyout menu can be wider than the trigger input (useful for long option text like timezone names) */
  wideFlyout?: boolean;
  /** Max flyout height in px before scrolling; grows up to available viewport space */
  flyoutMaxHeight?: number;
  /** Optional sticky content rendered above the options list */
  headerContent?: React.ReactNode;
  /** When provided, eligible options show a pin toggle control */
  isOptionPinnable?: (value: string) => boolean;
  isOptionPinned?: (value: string) => boolean;
  onToggleOptionPin?: (value: string) => void;
  /** Accessible name when no visible label is shown */
  ariaLabel?: string;
  /** Native title tooltip for the trigger input */
  title?: string;
  /** If true, the input automatically receives focus on mount */
  autoFocus?: boolean;
  /** On touch, open the menu before focusing the searchable input so mobile keyboards do not appear immediately. */
  openOnTouchWithoutFocus?: boolean;
  /** Optional compact trigger. Search remains available inside the flyout. */
  trigger?: SearchableDropdownTrigger;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  id,
  label,
  value,
  onChange,
  options,
  placeholder = 'Search and select...',
  required = false,
  error,
  className = '',
  containerClassName,
  inputClassName = '',
  disabled = false,
  isSearchable,
  isLoadingOptions = false,
  loadingMessage = 'Loading…',
  isUpward = false,
  size = 'md',
  allowCustomValue = true,
  showCheckmark = true,
  footerAction,
  wideFlyout = false,
  flyoutMaxHeight = 240,
  headerContent,
  isOptionPinnable,
  isOptionPinned,
  onToggleOptionPin,
  ariaLabel,
  title,
  autoFocus = false,
  openOnTouchWithoutFocus = false,
  trigger,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const lastSelectionRef = useRef(false);
  const closeViaToggleRef = useRef(false);

  const effectivelySearchable = isSearchable ?? options.length > 10;

  // Floating UI setup
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    placement: isUpward ? 'top-start' : 'bottom-start',
    middleware: [
      offset(5),
      flip({ padding: 5 }),
      shift({ padding: 5 }),
      sizeMiddleware({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            maxHeight: `min(${availableHeight - 10}px, ${flyoutMaxHeight}px)`,
            ...(wideFlyout
              ? {
                  minWidth: `${rects.reference.width}px`,
                  width: 'max-content',
                  maxWidth: 'min(420px, calc(100vw - 24px))',
                }
              : { width: `${rects.reference.width}px` }),
          });
        },
      }),
    ],
  });

  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'listbox' });
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, role]);

  // Reset any user-typed search term when parent-controlled value changes.
  useEffect(() => {
    setInputValue(null);
    setHighlightedIndex(-1);
  }, [value]);

  // Derive display value
  const displayInputValue = useMemo(() => {
    if (inputValue !== null) {
      return inputValue;
    }
    const matched = options.find((o) => o.value === value);
    return matched ? matched.label : value;
  }, [inputValue, value, options]);

  // Filter options
  const filteredOptions = useMemo(() => {
    if (!effectivelySearchable) {
      return options;
    }
    // Show all options if the user hasn't typed anything yet
    if (inputValue === null) {
      return options;
    }
    const lower = inputValue.toLowerCase();
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(lower) || option.value.toLowerCase().includes(lower)
    );
  }, [inputValue, options, effectivelySearchable]);

  const optionSections = useMemo(() => {
    const sections: Array<{
      label?: string;
      options: Array<{ option: SearchableDropdownOption; index: number }>;
    }> = [];

    filteredOptions.forEach((option, index) => {
      const currentSection = sections[sections.length - 1];
      if (!currentSection || currentSection.label !== option.section) {
        sections.push({
          label: option.section,
          options: [{ option, index }],
        });
        return;
      }

      currentSection.options.push({ option, index });
    });

    return sections;
  }, [filteredOptions]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!effectivelySearchable) return;
    const newValue = e.target.value;
    setInputValue(newValue);
    if (!isOpen) setIsOpen(true);
  };

  const commitValue = (newValue: string) => {
    onChange(newValue);
    setIsOpen(false);
  };

  const handleOptionSelect = (option: SearchableDropdownOption) => {
    if (option.disabled) return;
    lastSelectionRef.current = true;
    setInputValue(option.label);
    commitValue(option.value);
    inputRef.current?.blur();
    triggerRef.current?.focus();
  };

  const handleToggleTrigger = () => {
    if (disabled) return;

    if (isOpen) {
      closeViaToggleRef.current = true;
      setIsOpen(false);
      setInputValue(null);
      setHighlightedIndex(-1);
      inputRef.current?.blur();
      return;
    }

    setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        if (allowCustomValue) {
          commitValue(displayInputValue);
          return;
        }

        const exactMatch = options.find(
          (option) =>
            option.label.toLowerCase() === displayInputValue.toLowerCase() ||
            option.value.toLowerCase() === displayInputValue.toLowerCase()
        );

        if (exactMatch) {
          handleOptionSelect(exactMatch);
        } else {
          setInputValue(null);
        }
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
        break;
      case 'Enter': {
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          const opt = filteredOptions[highlightedIndex];
          if (opt.disabled) return;
          lastSelectionRef.current = true;
          setInputValue(opt.label);
          commitValue(opt.value);
        } else {
          if (allowCustomValue) {
            commitValue(displayInputValue);
            return;
          }

          const exactMatch = filteredOptions.find(
            (option) =>
              option.label.toLowerCase() === displayInputValue.toLowerCase() ||
              option.value.toLowerCase() === displayInputValue.toLowerCase()
          );

          if (exactMatch) {
            handleOptionSelect(exactMatch);
          } else {
            setIsOpen(false);
            setInputValue(null);
            setHighlightedIndex(-1);
          }
        }
        break;
      }
      case 'Escape':
        setIsOpen(false);
        triggerRef.current?.focus();
        break;
    }
  };

  const renderInput = (insideFlyout: boolean) => (
    <input
      ref={inputRef}
      id={id}
      type="text"
      className={[
        SINGLE_LINE_FIELD_CLASS,
        'flex w-full cursor-pointer items-center justify-between text-sm transition-all duration-fast',
        size === 'sm' ? 'h-9 px-3' : 'h-input-height px-4',
        !insideFlyout && (size === 'sm' ? 'pr-8' : 'pr-10'),
        !effectivelySearchable ? 'cursor-pointer caret-transparent' : '',
        disabled
          ? 'cursor-default border-outline-default/40 bg-input/60 text-glyph-primary/60'
          : '',
        insideFlyout ? 'min-w-64' : inputClassName,
      ].join(' ')}
      value={
        effectivelySearchable
          ? displayInputValue
          : options.find((option) => option.value === value)?.label || value
      }
      onChange={handleInputChange}
      onPointerDown={(event) => {
        if (
          disabled ||
          !openOnTouchWithoutFocus ||
          !effectivelySearchable ||
          isOpen ||
          event.pointerType === 'mouse'
        ) {
          return;
        }

        event.preventDefault();
        setIsOpen(true);
      }}
      onFocus={(event) => {
        if (effectivelySearchable) {
          event.target.select();
          setIsOpen(true);
        }
      }}
      onKeyDown={handleKeyDown}
      onBlur={() => {
        if (lastSelectionRef.current) {
          lastSelectionRef.current = false;
          return;
        }
        if (closeViaToggleRef.current) {
          closeViaToggleRef.current = false;
          return;
        }
        if (!effectivelySearchable) {
          setIsOpen(false);
          setInputValue(null);
          setHighlightedIndex(-1);
          return;
        }
        if (allowCustomValue) {
          commitValue(displayInputValue);
          return;
        }

        setIsOpen(false);
        setInputValue(null);
        setHighlightedIndex(-1);
      }}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={!effectivelySearchable}
      aria-label={ariaLabel || label || placeholder}
      title={title}
      autoComplete="off"
      autoFocus={insideFlyout || autoFocus}
    />
  );

  return (
    <InputContainer className={containerClassName ?? className}>
      <InputLabel label={label} required={required} htmlFor={id} />

      {trigger ? (
        <button
          ref={(node) => {
            triggerRef.current = node;
            refs.setReference(node);
          }}
          type="button"
          aria-label={trigger.ariaLabel}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          disabled={disabled}
          className={trigger.className}
          onClick={handleToggleTrigger}
        >
          {trigger.content}
        </button>
      ) : (
        <div
          className="relative w-full"
          ref={refs.setReference}
          {...getReferenceProps()}
          onMouseDown={(e) => {
            if (disabled) return;
            // For non-searchable, toggle on click anywhere except the chevron (handled separately).
            if (!effectivelySearchable) {
              const target = e.target as HTMLElement;
              if (
                !target.closest('[data-dropdown-options]') &&
                !target.closest('[data-dropdown-trigger-toggle]')
              ) {
                e.preventDefault();
                handleToggleTrigger();
              }
            }
          }}
        >
          {renderInput(false)}

          <button
            type="button"
            data-dropdown-trigger-toggle
            aria-label={isOpen ? 'Hide options' : 'Show options'}
            aria-expanded={isOpen}
            disabled={disabled}
            className={[
              'absolute top-1/2 flex -translate-y-1/2 items-center justify-center rounded-full text-glyph-secondary transition-colors hover:text-glyph-primary',
              size === 'sm' ? 'right-1 h-7 w-7' : 'right-1.5 h-8 w-8',
              disabled ? 'cursor-default opacity-60' : 'cursor-pointer',
            ].join(' ')}
            onMouseDown={(event) => {
              if (disabled) return;
              event.preventDefault();
              event.stopPropagation();
              handleToggleTrigger();
            }}
          >
            <ChevronDown
              size={size === 'sm' ? 14 : 16}
              strokeWidth={2.5}
              className={[
                'transition-transform duration-fast ease-smooth',
                isOpen ? 'rotate-180' : '',
              ].join(' ')}
              aria-hidden="true"
            />
          </button>
        </div>
      )}

      <FloatingPortal>
        {isOpen && !disabled && (
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              display: 'flex',
              flexDirection: 'column',
              zIndex: 'var(--z-menu)',
            }}
            {...getFloatingProps()}
          >
            <div
              data-dropdown-options
              className={`animate-xp-flyout-enter ${SELECTOR_FLYOUT_CHROME_CLASS} flex min-h-0 flex-col overflow-hidden`}
            >
              {trigger ? (
                <div className="shrink-0 border-b border-outline-default/40 p-2">
                  {renderInput(true)}
                </div>
              ) : null}
              {headerContent && (
                <div
                  className="shrink-0 border-b border-outline-default/40 px-2 py-2"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  {headerContent}
                </div>
              )}
              <div
                className={`min-h-0 flex-1 overflow-y-auto ${
                  optionSections.some((section) => section.label) ? 'space-y-2' : 'space-y-1'
                }`}
              >
                {filteredOptions.length > 0
                  ? optionSections.map((section, sectionIndex) => {
                      const sectionLabelId = `${id}-section-${sectionIndex}`;
                      const sectionOptions = section.options.map(({ option, index }) => (
                        <div
                          key={option.value}
                          role="option"
                          aria-selected={option.value === value}
                          aria-disabled={option.disabled || undefined}
                          className={[
                            SELECTOR_OPTION_CLASS,
                            option.disabled
                              ? 'cursor-not-allowed opacity-50'
                              : getSelectorOptionStateClass({
                                  selected: option.value === value,
                                  highlighted: index === highlightedIndex,
                                }),
                          ].join(' ')}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleOptionSelect(option);
                          }}
                          onMouseEnter={() => setHighlightedIndex(index)}
                        >
                          <div className="flex min-w-0 flex-1 items-center gap-2">
                            {!option.hideOptionLabel && (
                              <span className="min-w-0 break-words whitespace-normal">
                                {option.label}
                              </span>
                            )}
                            {option.tag && (
                              <span
                                className={[
                                  'shrink-0 rounded border px-1.5 py-0.5 text-xs font-medium tracking-wider uppercase',
                                  option.tagClassName ??
                                    'border-outline-subtle bg-neutral text-glyph-secondary',
                                ].join(' ')}
                              >
                                {option.tag}
                              </span>
                            )}
                          </div>
                          <div className="flex min-w-0 items-center gap-2">
                            {showCheckmark && option.value === value && (
                              <Check className="h-4 w-4 shrink-0 text-accent-primary" />
                            )}
                            {isOptionPinnable?.(option.value) && onToggleOptionPin && (
                              <button
                                type="button"
                                className={[
                                  'shrink-0 rounded p-1 transition-colors hover:bg-subtle focus-visible:ring-2 focus-visible:ring-accent-primary/30 focus-visible:outline-none',
                                  isOptionPinned?.(option.value)
                                    ? 'text-accent-primary'
                                    : 'text-glyph-tertiary hover:text-glyph-secondary',
                                ].join(' ')}
                                aria-label={
                                  isOptionPinned?.(option.value)
                                    ? `Unpin ${option.label}`
                                    : `Pin ${option.label}`
                                }
                                aria-pressed={isOptionPinned?.(option.value) ?? false}
                                onMouseDown={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  onToggleOptionPin(option.value);
                                }}
                              >
                                <Pin
                                  size={14}
                                  strokeWidth={2.25}
                                  className={isOptionPinned?.(option.value) ? 'fill-current' : ''}
                                />
                              </button>
                            )}
                          </div>
                        </div>
                      ));

                      if (!section.label) {
                        return (
                          <React.Fragment key={`options-${sectionIndex}`}>
                            {sectionOptions}
                          </React.Fragment>
                        );
                      }

                      return (
                        <div
                          key={`${section.label}-${sectionIndex}`}
                          role="group"
                          aria-labelledby={sectionLabelId}
                          className="space-y-1 border-t border-outline-default/40 pt-2 first:border-t-0 first:pt-0"
                        >
                          <div
                            id={sectionLabelId}
                            className="type-utility-label px-4 py-1 text-glyph-tertiary"
                          >
                            {section.label}
                          </div>
                          {sectionOptions}
                        </div>
                      );
                    })
                  : !isLoadingOptions && (
                      <div className="px-3 py-2 text-sm text-glyph-secondary italic">
                        No options found
                      </div>
                    )}
                {isLoadingOptions && (
                  <div
                    className="px-3 py-2 text-sm text-glyph-secondary"
                    role="status"
                    aria-live="polite"
                  >
                    {loadingMessage}
                  </div>
                )}
              </div>
              {footerAction && (
                <div
                  className="shrink-0 border-t border-outline-default/40 bg-raised/95 px-2 py-2"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  {footerAction}
                </div>
              )}
            </div>
          </div>
        )}
      </FloatingPortal>

      <InputError error={error} />
    </InputContainer>
  );
};

export default SearchableDropdown;
